'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useDocumentScrollProgress, useScrollSpy } from '@/hooks/use-scroll-spy';
import {
  getDocsScrollMarkers,
  getDocsSidebarItems,
  getOverviewToc,
  getTopicToc,
  resolveDocsSidebarSlug,
  scrollToDocsSection,
  type DocsTocItem,
} from '@/lib/docs-content';
import { docNavItems } from '@/lib/marketing-content';

type DocsScrollContextValue = {
  activeSectionId: string;
  activeSidebarSlug: string;
  activeSidebarIndex: number;
  reachedSidebarIndex: number;
  sidebarProgress: number;
  tocItems: DocsTocItem[];
  tocProgress: number;
  activeTocIndex: number;
  majorSectionIds: string[];
  registerScrollRoot: (node: HTMLElement | null) => void;
};

const DocsScrollContext = createContext<DocsScrollContextValue | null>(null);

export function useDocsScroll() {
  const value = useContext(DocsScrollContext);
  if (!value) {
    throw new Error('useDocsScroll must be used within DocsScrollProvider');
  }
  return value;
}

function subsectionProgress(activeSidebarSlug: string, activeSectionId: string): number {
  if (activeSidebarSlug === 'introduction') {
    if (activeSectionId === 'introduction') return 0;
    const toc = getOverviewToc();
    const index = toc.findIndex((item) => item.id === activeSectionId);
    if (index < 0) return 0.2;
    return (index + 1) / toc.length;
  }

  const topic = docNavItems.find((item) => item.id === activeSidebarSlug);
  if (!topic) return 0;

  if (activeSectionId === topic.id) return 0.12;

  const toc = getTopicToc(topic);
  const index = toc.findIndex((item) => item.id === activeSectionId);
  if (index < 0) return 0.25;
  return (index + 1) / toc.length;
}

export function DocsScrollProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const [usePanelScroll, setUsePanelScroll] = useState(false);
  const syncingHash = useRef(false);

  const registerScrollRoot = useCallback((node: HTMLElement | null) => {
    setScrollRoot(node);
  }, []);

  const sidebarItems = useMemo(() => getDocsSidebarItems(), []);
  const majorSectionIds = useMemo(
    () => ['introduction', ...docNavItems.map((topic) => topic.id)],
    [],
  );
  const sectionIds = useMemo(() => getDocsScrollMarkers(), []);

  const scrollTarget = usePanelScroll && scrollRoot ? scrollRoot : null;
  const trackingReady = ready && (!usePanelScroll || scrollRoot !== null);
  const spyOffset = scrollTarget ? 32 : 112;

  const activeSectionId = useScrollSpy(sectionIds, {
    enabled: trackingReady,
    root: scrollTarget,
    offset: spyOffset,
  });
  const scrollProgress = useDocumentScrollProgress(trackingReady, scrollTarget);
  const activeSidebarSlug = resolveDocsSidebarSlug(activeSectionId);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const sync = () => setUsePanelScroll(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    setReady(true);
  }, []);

  const activeSidebarIndex = Math.max(
    0,
    sidebarItems.findIndex((item) => item.slug === activeSidebarSlug),
  );
  const reachedSidebarIndex = activeSidebarIndex;
  const sectionFraction = subsectionProgress(activeSidebarSlug, activeSectionId);

  const sidebarProgress = useMemo(() => {
    if (scrollTarget) {
      const indexProgress =
        sidebarItems.length <= 1 ? 0 : activeSidebarIndex / (sidebarItems.length - 1);
      return Math.min(1, scrollProgress * 0.55 + indexProgress * 0.35 + sectionFraction * 0.1);
    }

    const indexProgress =
      sidebarItems.length <= 1 ? 0 : activeSidebarIndex / (sidebarItems.length - 1);
    return Math.min(1, indexProgress * 0.75 + sectionFraction * 0.15 + scrollProgress * 0.1);
  }, [
    activeSidebarIndex,
    scrollProgress,
    scrollTarget,
    sectionFraction,
    sidebarItems.length,
  ]);

  const tocItems = useMemo(() => {
    if (activeSidebarSlug === 'introduction') {
      return getOverviewToc();
    }

    const topic = docNavItems.find((item) => item.id === activeSidebarSlug);
    return topic ? getTopicToc(topic) : getOverviewToc();
  }, [activeSidebarSlug]);

  const activeTocIndex = useMemo(() => {
    if (activeSectionId === 'introduction') return -1;
    const topic = docNavItems.find((item) => item.id === activeSectionId);
    if (topic) return 0;
    return tocItems.findIndex((item) => item.id === activeSectionId);
  }, [activeSectionId, tocItems]);

  const tocProgress = useMemo(() => {
    if (activeTocIndex < 0) return scrollTarget ? scrollProgress : 0;
    if (tocItems.length <= 1) return activeTocIndex >= 0 ? 1 : 0;
    return Math.min(1, activeTocIndex / (tocItems.length - 1));
  }, [activeTocIndex, scrollProgress, scrollTarget, tocItems.length]);

  useEffect(() => {
    if (!trackingReady) return;

    const hash = window.location.hash.slice(1);
    if (!hash) return;

    syncingHash.current = true;
    scrollToDocsSection(hash, { behavior: 'auto' });
    window.setTimeout(() => {
      syncingHash.current = false;
    }, 400);
  }, [trackingReady, scrollRoot, usePanelScroll]);

  useEffect(() => {
    if (syncingHash.current || !activeSectionId) return;
    const nextHash = `#${activeSectionId}`;
    if (window.location.hash === nextHash) return;
    window.history.replaceState(null, '', `${window.location.pathname}${nextHash}`);
  }, [activeSectionId]);

  const value = useMemo(
    () => ({
      activeSectionId,
      activeSidebarSlug,
      activeSidebarIndex,
      reachedSidebarIndex,
      sidebarProgress,
      tocItems,
      tocProgress,
      activeTocIndex,
      majorSectionIds,
      registerScrollRoot,
    }),
    [
      activeSectionId,
      activeSidebarSlug,
      activeSidebarIndex,
      reachedSidebarIndex,
      sidebarProgress,
      tocItems,
      tocProgress,
      activeTocIndex,
      majorSectionIds,
      registerScrollRoot,
    ],
  );

  return <DocsScrollContext.Provider value={value}>{children}</DocsScrollContext.Provider>;
}
